/**
 * The browser's `@/lib/db`: the same exports as `apps/mobile/src/lib/db.ts`,
 * over SQLite in WebAssembly instead of `expo-sqlite`.
 *
 * Everything that reads or writes the mirror (`entry-repo.ts`, the insight
 * compilers, search, the outbox) is the phone's own code and reaches the
 * database only through this module. So this file is the whole difference
 * between the phone's storage and the browser's.
 *
 * Two things differ from the phone, and both are forced by the browser:
 *
 *   1. **The database lives in memory.** It is restored from IndexedDB when
 *      it opens and saved back a moment after each burst of writes, and again
 *      when the tab is hidden. See snapshot.ts.
 *   2. **There is no second connection.** The phone opens a reader so a long
 *      query leaves the JS thread free. The browser has one thread either way,
 *      so the async readers yield once and then run on the same connection.
 *   3. **It opens only for a session that needs it.** SQLite is about 900 kB
 *      (its WebAssembly and its script). A signed-out page needs none of it,
 *      and on a slow phone it held the sign-in page back by seconds. So the
 *      script is imported, and the database opened, by `openDatabase()`: at
 *      once when this browser already holds a mirror (`MIRROR_HINT`, and the
 *      build preloads both files for it), otherwise when the gate in
 *      `~/app/root-providers.tsx` sees a signed-in session. Until then
 *      `getDb()` answers an empty database that cannot be written to
 *      (`sqlite/empty.ts`).
 */
import type { Sqlite3Static } from '@sqlite.org/sqlite-wasm';
import { drizzle, type ExpoSQLiteDatabase } from 'drizzle-orm/expo-sqlite/driver';

import { DATABASE_VERSION, MIGRATIONS } from '@/lib/db-migrations';
import * as schema from '@/lib/db-schema';

import { openWebDatabase, type BindParams, type WebSqliteDatabase } from './sqlite/adapter';
import { MIRROR_HINT } from './mirror-hint';
import { EmptySqliteDatabase } from './sqlite/empty';
import { loadSnapshot, saveSnapshot } from './sqlite/snapshot';

export type DeviceDb = ExpoSQLiteDatabase<typeof schema> & { $client: WebSqliteDatabase };

/** How long the database stays quiet before its snapshot is written. */
const SAVE_AFTER_MS = 300;
/** The longest a snapshot waits while writes keep coming, as they do through a long first sync. */
const SAVE_AT_MOST_MS = 2000;

const readHint = (): boolean => {
  try {
    return localStorage.getItem(MIRROR_HINT) === '1';
  } catch {
    return false;
  }
};

const writeHint = (held: boolean): void => {
  try {
    if (held) localStorage.setItem(MIRROR_HINT, '1');
    else localStorage.removeItem(MIRROR_HINT);
  } catch {
    // A private window or blocked storage: the next visit opens the database later, nothing worse.
  }
};

/** Whether this browser held a mirror when the page loaded. Read once: the gate keys on it. */
export const heldMirrorAtLoad = readHint();

/**
 * Bring the database up to DATABASE_VERSION, exactly as the phone does. A
 * snapshot from a newer build than this one is not trusted: it is replaced.
 */
const migrate = (sqlite3: Sqlite3Static, client: WebSqliteDatabase): WebSqliteDatabase => {
  const current = client.getFirstSync<{ user_version: number }>('PRAGMA user_version')?.user_version ?? 0;
  if (current > DATABASE_VERSION) {
    client.close();
    return migrate(sqlite3, openWebDatabase(sqlite3, null));
  }
  for (let step = current; step < DATABASE_VERSION; step += 1) {
    const statements = MIGRATIONS[step];
    if (statements !== undefined) client.execSync(statements);
  }
  client.execSync(`PRAGMA user_version = ${DATABASE_VERSION}`);
  return client;
};

let cached: DeviceDb | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let savedAt = -1;

/** The newest snapshot write asked for; each waits for the one before it. Never rejects. */
let lastWrite: Promise<boolean> = Promise.resolve(true);

/**
 * Writes the snapshot now, if anything changed since the last one. Answers
 * whether the copy in IndexedDB now holds what the open database holds.
 */
export const flushSnapshot = async (): Promise<boolean> => {
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  firstUnsavedAt = null;
  const client = cached?.$client;
  if (client === undefined) return false;
  // Nothing new: the copy holds this once the last queued write lands.
  if (client.writeCount === savedAt) return lastWrite;
  const writing = client.writeCount;
  savedAt = writing;
  // Set before the copy is written: a tab that closes mid-save must leave a
  // browser that opens the database, and so wipes it, on its next visit.
  writeHint(true);
  // In the order they were asked for: an older copy still being written must
  // never land after a newer one (a save begun before a sign-out's wipe would
  // put the last person's file back).
  const bytes = client.exportBytes();
  const write = lastWrite.then(() => saveSnapshot(bytes));
  lastWrite = write;
  const written = await write;
  // Not written: the next flush tries again rather than counting it as saved.
  if (!written && savedAt === writing) savedAt = -1;
  return written;
};

let firstUnsavedAt: number | null = null;

/** Debounced: a burst of writes makes one snapshot, and a long burst one every two seconds. */
const scheduleSave = (): void => {
  const now = Date.now();
  firstUnsavedAt ??= now;
  if (saveTimer !== null) clearTimeout(saveTimer);
  const wait = Math.max(0, Math.min(SAVE_AFTER_MS, firstUnsavedAt + SAVE_AT_MOST_MS - now));
  saveTimer = setTimeout(() => {
    saveTimer = null;
    firstUnsavedAt = null;
    void flushSnapshot();
  }, wait);
};

const listeners = new Set<() => void>();
let opening: Promise<void> | null = null;

/** Whether the real database is open. */
export const databaseIsOpen = (): boolean => cached !== null;

/** Told once, when the database opens (for `useSyncExternalStore`). */
export const onDatabaseOpen = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Imports SQLite, reads the IndexedDB copy beside it, migrates, and opens.
 * Once: every later call answers the same promise, unless it failed.
 */
export const openDatabase = (): Promise<void> => {
  opening ??= (async () => {
    // Side by side: the IndexedDB read never waits on the WebAssembly, nor it on the read.
    const [{ default: sqlite3InitModule }, snapshot] = await Promise.all([import('@sqlite.org/sqlite-wasm'), loadSnapshot()]);
    const sqlite3 = await sqlite3InitModule();
    const client = migrate(sqlite3, openWebDatabase(sqlite3, snapshot));
    client.execSync('PRAGMA foreign_keys = ON;');
    // A deleted row's bytes are zeroed, not left in a free page: the snapshot
    // is the whole file, and after a sign-out it held the last person's
    // records and names in pages no table pointed at any more.
    client.execSync('PRAGMA secure_delete = ON;');
    client.onWrite(scheduleSave);
    // The drizzle driver only ever calls the sync methods the adapter implements.
    cached = drizzle(client as unknown as Parameters<typeof drizzle>[0], { schema }) as unknown as DeviceDb;
    savedAt = client.writeCount;
    writeHint(true);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void flushSnapshot();
      });
      window.addEventListener('pagehide', () => void flushSnapshot());
    }
    for (const listener of listeners) listener();
  })().catch((error: unknown) => {
    // A failed open is not kept: the next call tries again.
    opening = null;
    throw error;
  });
  return opening;
};

/**
 * A sign-out wiped the mirror (`forgetDevice`): saves the wiped copy, then
 * drops the hint, so the next visit's signed-out pages need no SQLite. Only
 * once the save has landed, and only if nothing was written since: a hint
 * dropped while the last person's copy is still in IndexedDB (a save that
 * failed, or a tab closed mid-save) would leave it there for the next sign-in
 * to load. A write that lands while the copy saves (the wipe run again, as
 * StrictMode runs every effect twice) is saved too before the hint goes;
 * without that the hint stayed and the next signed-out visit loaded SQLite.
 */
export const forgetMirror = async (): Promise<void> => {
  for (let tries = 0; tries < 5; tries += 1) {
    const saved = await flushSnapshot();
    const client = cached?.$client;
    if (!saved || client === undefined) return;
    if (client.writeCount === savedAt) {
      writeHint(false);
      return;
    }
  }
};

let empty: DeviceDb | null = null;

/** The open database, or, before it opens, the empty one (`sqlite/empty.ts`). */
export const getDb = (): DeviceDb => {
  if (cached !== null) return cached;
  empty ??= drizzle(new EmptySqliteDatabase() as unknown as Parameters<typeof drizzle>[0], { schema }) as unknown as DeviceDb;
  return empty;
};

// A browser that holds a mirror opens it straight away, beside the page's own
// scripts. A failure here is the gate's to show: it calls again and catches it.
if (heldMirrorAtLoad) openDatabase().catch(() => undefined);

export const execRaw = (statement: string): void => {
  getDb().$client.execSync(statement);
};

export const queryRaw = <T>(statement: string, params: BindParams): T[] =>
  getDb().$client.getAllSync<T>(statement, params);

/** Yields once so the caller's frame can paint, then reads on the one connection. */
export const queryRawAsync = async <T>(statement: string, params: BindParams): Promise<T[]> => {
  await Promise.resolve();
  return queryRaw<T>(statement, params);
};

export const execRawAsync = async (statement: string): Promise<void> => {
  await Promise.resolve();
  execRaw(statement);
};

export const runRaw = (statement: string, params: BindParams): void => {
  getDb().$client.runSync(statement, params);
};

export const withPrepared = <T>(statement: string, work: (run: (params: BindParams) => void) => T): T => {
  const prepared = getDb().$client.prepareSync(statement);
  try {
    return work((params) => {
      prepared.executeSync(params);
    });
  } finally {
    prepared.finalizeSync();
  }
};
